import React, { useState, ChangeEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import GlobalSearch from "../common/GlobalSearch";
import { Clear, FilterIcon } from "../common/ProjectIcons";
import SelectList from "../common/SelectList";
import { DestinationType, Organization, ProviderType } from "../common/ObjectTypes";
import { filterClasses, buttonClasses } from "../common/tailwindClassnames";

type SourceFilterProps = {
    filters: any[],
    globalSearchStr?: string,
    organizations: Organization[],
    destinationUrls: string[],
    providerTypes: ProviderType[],
    destinationTypes: DestinationType[],
    loadingProviderTypes?: boolean,
    loadMoreProviderTypes?: boolean,
    onLoadMoreProviderTypes?: Function,
    onChangeOwner?: ChangeEventHandler,
    onChangeOwnerSearchBar?: ChangeEventHandler,
    onLoadMoreOrganizations?: Function,
    loadingOrganizations?: boolean,
    loadMoreOrganizations?: boolean,
    onChangeDestinationType?: ChangeEventHandler,
    onLoadMoreDestinationTypes?: Function,
    onChangeProviderType?: ChangeEventHandler,
    onChangeEndpoint: ChangeEventHandler,
    onChangeDestinationUrlSearchBar: ChangeEventHandler,
    loadMoreDestinationUrls: boolean,
    loadingDestinationUrls: boolean,
    onLoadMoreDestinationUrls: Function,
    onChangeProviderTypeSearchBar?: ChangeEventHandler,
    onChangeGlobalSearch: Function,
    onRemoveTag: any,
    onResetFilters: any,
    showOrgFilter: boolean
    showProviderTypeFilter: boolean,
    showDestinationTypeFilter: boolean,
    onChangeDestinationTypeSearchBar?: Function,
    loadingDestinationTypes?: boolean,
    loadMoreDestinationTypes?: boolean
};

const SourceFilter = ({
    filters,
    globalSearchStr,
    organizations,
    destinationUrls,
    providerTypes,
    destinationTypes,
    loadMoreProviderTypes,
    loadingProviderTypes,
    onLoadMoreProviderTypes,
    onChangeOwner,
    onChangeOwnerSearchBar,
    loadingOrganizations,
    loadMoreOrganizations,
    onLoadMoreOrganizations,
    onChangeDestinationType,
    onChangeProviderType,
    onChangeDestinationUrlSearchBar,
    loadMoreDestinationUrls,
    loadingDestinationUrls,
    onLoadMoreDestinationUrls,
    onChangeProviderTypeSearchBar,
    onChangeEndpoint,
    onChangeGlobalSearch,
    onRemoveTag,
    onResetFilters,
    showOrgFilter,
    showProviderTypeFilter,
    showDestinationTypeFilter,
    onLoadMoreDestinationTypes,
    onChangeDestinationTypeSearchBar,
    loadingDestinationTypes,
    loadMoreDestinationTypes
}: SourceFilterProps) => {
    const { t } = useTranslation();

    return (
        <div className={filterClasses.container}>
            <GlobalSearch
                onChangeGlobalSearch={onChangeGlobalSearch}
                savedString={globalSearchStr}
            />
            <div className={filterClasses.header}>
                <h3><FilterIcon /> {t("Filters")}</h3>
                {filters.length > 0 && <h3 className={filterClasses.tagCount}>{filters.length}</h3>}
            </div>
            {/* tags of all of the filters */}
            {filters.length > 0 &&
                <div className="flex flex-col mt-3 pt-3">
                    {filters.map((item: any) => {
                        return (
                            <p className={filterClasses.tag}
                                key={item.id}
                            >
                                {t(item.name)}
                                <span className="pb-3 text-dark-green dark:text-dark-green-100 cursor-pointer transition-colors duration-300"
                                    onClick={() => onRemoveTag(item.id)}>
                                    <Clear />
                                </span>
                            </p>
                        )
                    })}
                    <button className={buttonClasses.resetFilter}
                        onClick={onResetFilters}
                    >{t("Clear All")}</button>
                </div>}
            {/* provider */}
            {showProviderTypeFilter && <div className={filterClasses.section}>
                <SelectList
                    optionList={providerTypes.map((provider) => {
                        return {
                            name: provider.name,
                            id: provider.value,
                            attribute_name: "provider_type__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeProviderType}
                    onChangeSearchBar={onChangeProviderTypeSearchBar}
                    sectionLabel={t("Filter by Technology")}
                    loadMore={loadMoreProviderTypes || false}
                    loading={loadingProviderTypes || false}
                    onClickLoadMore={() => onLoadMoreProviderTypes && onLoadMoreProviderTypes()}
                />
            </div>}
            {/* destination url */}
            <div className={filterClasses.section}>
                <SelectList
                    optionList={destinationUrls.map((url) => {
                        let new_url: string | URL;
                        try {
                            new_url = new URL(url)
                        } catch (err) {
                            new_url = ""
                        }
                        return {
                            name: new_url instanceof URL ? new_url.hostname : "",
                            id: url,
                            attribute_name: "destination_url__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeEndpoint}
                    onChangeSearchBar={onChangeDestinationUrlSearchBar}
                    sectionLabel={t("Filter by Destination")}
                    loadMore={loadMoreDestinationUrls}
                    onClickLoadMore={() => onLoadMoreDestinationUrls()}
                    loading={loadingDestinationUrls}
                />
            </div>
            {/* destination type */}
            {showDestinationTypeFilter && <div className={filterClasses.section}>
                <SelectList
                    optionList={destinationTypes.map((dest) => {
                        return {
                            name: dest.name,
                            id: dest.value,
                            attribute_name: "destination_type__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeDestinationType}
                    sectionLabel={t("Destination Type")}
                    onChangeSearchBar={() => onChangeDestinationTypeSearchBar && onChangeDestinationTypeSearchBar()}
                    loadMore={loadMoreDestinationTypes || false}
                    onClickLoadMore={() => onLoadMoreDestinationTypes && onLoadMoreDestinationTypes()}
                    loading={loadingDestinationTypes || false}
                />
            </div>}
            {/* organization */}
            {showOrgFilter && <div className={filterClasses.section}>
                <SelectList
                    optionList={organizations.map((org) => {
                        return {
                            name: org.name,
                            id: org.id,
                            attribute_name: "owner__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeOwner}
                    sectionLabel={t("Filter by Workspace")}
                    loadMore={loadMoreOrganizations || false}
                    onChangeSearchBar={onChangeOwnerSearchBar}
                    onClickLoadMore={() => onLoadMoreOrganizations && onLoadMoreOrganizations()}
                    loading={loadingOrganizations || false}
                />
            </div>}
        </div>
    )
};

export default SourceFilter;